// playerConfig.js
'use strict';

import PRESTOplayPlayerAdapter from '../resource/PRESTOplayPlayerAdapter.js';
import { PlayerEngine } from '../selector/getCurrentEngine.js';
import env from '../resource/env.js';

export const LoadMode = Object.freeze({
  ACTIVE: 'ACTIVE',
  PRELOAD: 'PRELOAD',
  NONE: 'NONE',
});

export const PRIORITY = Object.freeze({
  ACTIVE: 10,
  PIP: 9,
  PRELOAD: 1,
  NONE: 0,
});

export const PRIORITY_BY_LOAD_MODE = Object.freeze({
  [LoadMode.ACTIVE]: PRIORITY.ACTIVE,
  [LoadMode.PRELOAD]: PRIORITY.PRELOAD,
  [LoadMode.NONE]: PRIORITY.NONE,
});

/** Map a PRIORITY value back to its LoadMode. */
export const getLoadModeFromPriority = priority => {
  if (priority === PRIORITY.ACTIVE) return LoadMode.ACTIVE;
  if (priority === PRIORITY.NONE) return LoadMode.NONE;
  return LoadMode.PRELOAD;
};

/** Build a string key from a source manifest object for equality checks. */
const buildSourceKey = sourceValue =>
  JSON.stringify([
    sourceValue?.dash || '',
    sourceValue?.hls || '',
    sourceValue?.mp4 || '',
  ]);

/** Build a string key from a DRM config object for equality checks. */
const buildDrmKey = drmValue =>
  JSON.stringify([
    drmValue?.keySystem || '',
    drmValue?.drmToken || '',
    drmValue?.robustness || '',
    drmValue?.widevineServerCertificateBase64 || '',
    drmValue?.drmLicenseDuration || '',
  ]);

/** Compare two source objects by manifest URLs and DRM config. */
const isSourceEqual = (sourceA, sourceB) =>
  buildSourceKey(sourceA?.src) === buildSourceKey(sourceB?.src) &&
  buildDrmKey(sourceA?.drm) === buildDrmKey(sourceB?.drm);

/**
 * Build the adapter entry for an engine, reading env at call time so a
 * GrowthBook override of PRESTOPLAY_LICENSE_KEY is picked up.
 * @param {string} engine - a PlayerEngine value.
 * @returns {?object} `{ AdapterClass, adapterConfig }`, or null if unsupported.
 * @example
 * getAdapterByEngine('prestoplay');
 * // => { AdapterClass: PRESTOplayPlayerAdapter, adapterConfig: { playerOptions: { license: 'eyJ…' } } }
 */
export const getAdapterByEngine = engine => {
  if (engine !== PlayerEngine.PRESTOPLAY) return null;

  return {
    AdapterClass: PRESTOplayPlayerAdapter,
    adapterConfig: {
      playerOptions: {
        license: env.PRESTOPLAY_LICENSE_KEY || '',
      },
    },
  };
};

/** pool=5 → ±2, pool=3 → ±1 */
const getPreloadOffset = poolSize => Math.floor(((poolSize || 1) - 1) / 2);

export { buildSourceKey, buildDrmKey, isSourceEqual, getPreloadOffset };
